"use client";

import React, { useEffect, useRef, useState } from "react";
import { HeartPulse, Search, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  title: "Positional Therapy",
  description: "Clinically-proven techniques and specialized pillows designed to keep you sleeping on your side, preventing the airway collapse often caused by gravity while on your back.",
  icon: HeartPulse
}, {
  title: "Lifestyle Adjustments",
  description: "Evidence-based modifications to sleep hygiene, alcohol consumption, and evening routines that naturally reduce inflammation and improve your overall airway health.",
  icon: Users
}, {
  title: "Weight Management",
  description: "Professional guidance on how even modest weight reduction can decrease tissue pressure in the neck, significantly improving breathing patterns and sleep quality.",
  icon: Search
}];
export default function YourTreatmentPlan() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="155892" id="cpap-alternatives" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block text-primary font-semibold tracking-wider uppercase text-sm mb-4">
            CPAP Alternatives
          </span>
          <h2 className="text-foreground font-sans text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Explore comfortable, non-invasive ways to reclaim your rest.
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans">
            Beyond the mask, there are effective lifestyle therapies designed to manage mild to moderate sleep apnea with ease and clinical precision.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {servicesData.map((service, i) => <div data-index={i} key={i} className="h-full">
              <Card className="group h-full bg-card border-border border-t-[4px] border-t-primary shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-[10px] flex flex-col">
                <CardHeader className="pb-4">
                  <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-secondary text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <service.icon className="size-10" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-foreground font-sans text-xl md:text-2xl font-bold leading-snug">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-muted-foreground text-base leading-relaxed font-sans">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}